import { useEffect, useRef, useState } from 'react';
import {
  BlokkieButton,
  blokkieTheme,
  BlokkieTypography,
} from '@tikkie/blokkie';

import { useFocus } from '@/hooks/focus';
import { useEscapeKey } from '@/hooks/escape-key';
import { useClickOutside } from '@/hooks/click-outside';

import './instruction-modal-button.css';

interface Props {
  onButtonClick?: () => void;
  onCloseButtonClick?: () => void;
  openButtonText: string;
  closeButtonText: string;
  instructionTitle: string;
  instructions: Record<string, string>;
  a11yInstructions?: Record<string, string>;
  buttonVariant?: 'primary' | 'secondary' | 'tertiary';
}

export function InstructionModalButton({
  onButtonClick,
  onCloseButtonClick,
  instructions,
  a11yInstructions,
  openButtonText,
  closeButtonText,
  instructionTitle,
  buttonVariant = 'secondary',
}: Props) {
  const modalRef = useRef<HTMLDialogElement>(null);

  const { focusById } = useFocus();

  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      onCloseButtonClick?.();
    }
    if (isOpen) {
      onButtonClick?.();
    }
  }, [isOpen, onButtonClick, onCloseButtonClick]);

  const closeModal = () => setIsOpen(false);
  const closeModalAndChangeFocus = () => {
    if (!isOpen) {
      return;
    }

    closeModal();
    focusById('open-modal');
  };

  const openModal = () => setIsOpen(true);
  const openModalAndChangeFocus = () => {
    openModal();

    setTimeout(() => {
      focusById('instruction-modal');
    });
  };

  useEscapeKey(() => closeModalAndChangeFocus());
  useClickOutside(modalRef, () => closeModal());

  return (
    <div>
      <BlokkieButton
        id="open-modal"
        aria-haspopup="dialog"
        aria-controls="helpDialog"
        aria-expanded="false"
        variant={buttonVariant}
        style={{
          position: 'relative',
          textDecoration: 'none',
          marginTop: blokkieTheme.spacing(4),
          zIndex: 1000,
        }}
        tabIndex={0}
        onClick={openModalAndChangeFocus}
        className="overflow-button"
      >
        {openButtonText}
      </BlokkieButton>

      {isOpen && <div className="instruction-modal__overlay"></div>}

      <dialog
        ref={modalRef}
        role="dialog"
        id="instruction-modal"
        aria-labelledby="instruction-modal-title"
        aria-describedby="instruction-modal-description"
        aria-modal="true"
        aria-hidden={!isOpen}
        tabIndex={-1}
        style={{
          position: 'fixed',
          inlineSize: 'auto',
          background: blokkieTheme.colors.primary.purple,
          border: 'none',
          padding: blokkieTheme.spacing(2),
          zIndex: 1000,
          right: 0,
          left: 0,
          bottom: 0,
          height: '100dvh',
          overflow: 'auto',
          borderTopRightRadius: blokkieTheme.spacing(3),
          borderTopLeftRadius: blokkieTheme.spacing(3),
          maxWidth: blokkieTheme.breakpoints.tablet.min,
          display: isOpen ? 'grid' : 'none',
          outline: 'none',
        }}
      >
        <div
          tabIndex={-1}
          style={{
            textAlign: 'left',
            paddingTop: blokkieTheme.spacing(2),
            paddingInline: blokkieTheme.spacing(1),
          }}
        >
          <BlokkieTypography
            variant="h3"
            tabIndex={0}
            id="instruction-modal-title"
          >
            {instructionTitle}
          </BlokkieTypography>

          <ol
            className="instruction-modal-button__instructions"
            id="instruction-modal-description"
            role="list"
          >
            {Object.entries(instructions).map(([key, instruction]) => (
              <li
                className="instruction-modal-button__instruction-item"
                key={`instruction-${key}`}
                tabIndex={0}
              >
                <BlokkieTypography
                  variant="pm500"
                  style={{
                    fontSize: '0.875rem',
                  }}
                  aria-label={a11yInstructions?.[key] || instruction}
                >
                  {instruction}
                </BlokkieTypography>
              </li>
            ))}
          </ol>
        </div>

        <BlokkieButton
          style={{
            margin: '0 auto',
            width: '100%',
            marginBottom: blokkieTheme.spacing(3),
            maxWidth: blokkieTheme.breakpoints.mediumPhone.min,
            alignSelf: 'end',
          }}
          variant="primary"
          onClick={() => closeModalAndChangeFocus()}
          className="overflow-button"
          id="close-modal"
          data-testid="close-modal"
          aria-label={closeButtonText}
        >
          <BlokkieTypography variant="buttonLarge">
            {closeButtonText}
          </BlokkieTypography>
        </BlokkieButton>
      </dialog>
    </div>
  );
}
